import { nonEmpty } from '@/react/view-models/Form/validation-utils';
import { msg } from '@/services/isomorphic/I18NService';
import { isFullNameDOMSCompatible } from '@/services/utils/order-validations/isFullNameDOMSCompatible';
import type { DTO } from '@/type-utils';
import { makeObservable, observable } from 'mobx';
import { FormModel, validatable } from '../../../Form';
import type { INamesForm } from './INamesForm';
import { forms_field_firstName } from "@/lang/__generated__/ahnu/forms_field_firstName";
import { forms_field_lastName } from "@/lang/__generated__/ahnu/forms_field_lastName";
import { checkout_shipping_errors_nameTooLong } from "@/lang/__generated__/ahnu/checkout_shipping_errors_nameTooLong";

/** Represents a form for customer names entry. */
export class NamesFormModel
  extends FormModel<INamesForm>
  implements INamesForm
{
  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_field_firstName);
    },
    validations: [nonEmpty]
  })
  public firstName: string;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_field_lastName);
    },
    validations: [nonEmpty]
  })
  public lastName: string;

  /** @inheritdoc */
  public constructor(dto: DTO<INamesForm>) {
    super(dto);

    this.firstName = dto.firstName;
    this.lastName = dto.lastName;

    this.addValidationBefore(({ firstName, lastName }) => {
      // This stops the user from advancing and displays an error if the
      // name is too long (firstName + ' ' + lastName > 30 characters).
      if (!isFullNameDOMSCompatible([firstName, lastName])) {
        return {
          isValid: false,
          errorMessage: msg(checkout_shipping_errors_nameTooLong)
        };
      }

      return {
        isValid: true,
        errorMessage: null
      };
    });

    makeObservable(this);
  }

  /** @inheritdoc */
  public toDTO(): DTO<INamesForm> {
    return {
      firstName: this.firstName,
      lastName: this.lastName
    };
  }
}
